import parser from './parser';
import { InhouseCaseEntity } from '@/types/common';
import { formatTime as _formatTime, timeLocales, Format } from '@shopee_common/time';
import { COUNTRY } from '../locale';

type ValueData = string | string[] | Record<string, unknown> | Record<string, unknown>[];

type FormatedFormData = {
  id: string;
  title: string;
  value: ValueData;
};

type Schema = {
  id?: string;
  type?: string;
  properties?: Schema;
  [key: string]: unknown;
};

class CaseMsgType {
  static isWeb(type: string) {
    return (type || '').toLowerCase() === 'web';
  }
  static isEmail(type: string) {
    return (type || '').toLowerCase() === 'email';
  }
  static isChat(type: string) {
    return (type || '').toLowerCase() === 'chat';
  }
}

function ignore() {
  return null;
}

const TIME_LOCALE = COUNTRY?.toUpperCase();
const timeLocale = timeLocales[TIME_LOCALE as keyof typeof timeLocales];

/**
 * 格式化时间
 */
export function formatTime(time: number | string | undefined) {
  if (time === undefined) {
    return '';
  }
  // 原来用的是dayjs，使用 new Date 替换
  // const val = typeof time === 'number' ? dayjs.unix(time) : dayjs(time);
  const val = typeof time === 'number' ? new Date(time * 1000) : new Date(time);
  return _formatTime(timeLocale, val, Format.DATE_TIME);
}

function formatPhone(value: unknown) {
  return Array.isArray(value) && (value as string[]).length >= 2
    ? (value as string[]).slice(0, 2).join(' ')
    : (value as string);
}

function formatEmail(value: unknown) {
  return Array.isArray(value) && (value as string[]).length >= 2 ? (value as string[])[1] || '' : (value as string);
}

enum WidgetType {
  DateTime = 'DateOrTime',
  Description = 'DescriptionArea',
  LivenessCheck = 'LivenessCheck',
  RequestEmail = 'RequestEmail',
  RequestPhone = 'RequestPhone',
  PhoneNumber = 'PhoneNumber',
  ShopeeUserName = 'ShopeeUserName',
  ProofFile = 'ProofFile',
}

const WidgetHandler = {
  [WidgetType.DateTime]: formatTime,
  [WidgetType.Description]: ignore, // ignore
  [WidgetType.LivenessCheck]: ignore, // ignore
  [WidgetType.RequestEmail]: formatEmail,
  [WidgetType.RequestPhone]: formatPhone,
  [WidgetType.ShopeeUserName]: formatEmail,
  [WidgetType.PhoneNumber]: formatPhone,
};

function hasSubSchema(schema: Schema): boolean {
  return !!(schema && schema.type === 'object' && schema.properties);
}

const flatten = (obj: Record<string, unknown>): Record<string, unknown> => {
  return Object.keys(obj).reduce((acc: Record<string, unknown>, k) => {
    acc[k] = obj[k];
    if (Object.prototype.toString.call(obj[k]) === '[object Object]') {
      Object.assign(acc, flatten(obj[k] as never));
    }
    return acc;
  }, {});
};

function flattenSchemas(
  schema: Schema,
  dataMap: Record<string, unknown>,
  uiMap: Record<string, unknown>
): FormatedFormData[] {
  const result: FormatedFormData[] = [];
  if (!hasSubSchema(schema)) {
    // 为了去除第一层数据而存在
    return result;
  }
  const properties = schema.properties || {};
  Object.keys(properties).forEach(key => {
    const item = (properties[key] || {}) as Schema;
    if (hasSubSchema(item)) {
      result.push(...flattenSchemas(item, dataMap, uiMap));
    } else {
      const ui = (uiMap[key] || {}) as Record<string, string>;
      const widget = ui['ui:widget'] as WidgetType;
      const handler = WidgetHandler[widget as never] as (value: unknown) => string | null;
      const value = handler ? handler(dataMap[key]) : dataMap[key];
      if (value === null) {
        // 过滤handler无法处理的数据
        return;
      } else if (ui['ui:hidden']) {
        // 忽略隐藏字段
        return;
      } else if (ui['ui:widget'] === WidgetType.DateTime) {
        // 时间类型字段需要格式化
        result.push({
          id: key,
          title: item.title as string,
          value: formatTime(dataMap[key] as string) as string,
        });
      } else {
        // 其他字段交由调用层处理
        result.push({
          id: key,
          title: item.title as string,
          value: dataMap[key] as ValueData,
        });
      }
    }
  });
  return result;
}

function getFormData(schema: Schema, data: Record<string, unknown>, ui: Record<string, unknown>): FormatedFormData[] {
  return flattenSchemas(schema, flatten(data), flatten(ui));
}

function getSummaryWithFormData(formData: FormatedFormData[]) {
  return formData
    .map(item => {
      const title = item.title || '';
      const value = item.value || '';
      if (Array.isArray(value)) {
        const list = (value || []).map(val => (typeof val === 'string' ? val : '')).filter(i => !!i);
        return list.length > 0 ? `${title}: [${list.join(',')}]` : '';
      } else {
        return `${title}: ${value}`;
      }
    })
    .filter(i => !!i)
    .join(' ');
}

export function getWebformData(webform: InhouseCaseEntity): FormatedFormData[] {
  try {
    const schema = JSON.parse(webform.form_schema || '');
    const values = JSON.parse(webform.submitted_data || '');
    const uiSchema = JSON.parse(webform.ui_schema || '');
    return getFormData(schema, values, uiSchema) || [];
  } catch {
    return [];
  }
}

export function getSummary(webform: InhouseCaseEntity) {
  try {
    const formData = getWebformData(webform) || [];
    return getSummaryWithFormData(formData);
  } catch {
    return '';
  }
}

export async function formatCaseDesc(caseEntity: InhouseCaseEntity) {
  let result = '';
  try {
    let text;
    if (CaseMsgType.isWeb(caseEntity.msg_type)) {
      // Inhouse Webform数据
      text = getSummary(caseEntity);
    } else {
      // 替换回车换行、格式化文件与图片的显示格式
      text = (caseEntity.case_desc || '').replace(/[\r\n]/g, ' ').replace(/\[(file|image).+?\]/g, '[$1]');
      // 提取富文本中的可读节点
      text = await parser.parseHtml(text);
    }
    result = text;
  } catch (e) {
    // Do noting
  }
  return result;
}
